import React, { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { LabelTemplate, FieldData, createSingleLabelConfig } from '@/types/label';
import { TemplatePresets } from '@/components/TemplatePresets';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { printLabels } from '@/lib/browserPrint';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { useTemplateStorage } from '@/hooks/useTemplateStorage';
import { useActivityLogger } from '@/hooks/useActivityLogger';
import { useLabelHistory } from '@/hooks/useLabelHistory';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  Printer, 
  RotateCcw, 
  ArrowLeft,
  ScanBarcode,
  CheckCircle2,
  Plus,
  Minus,
  LayoutTemplate,
  FolderOpen,
  Sparkles,
  Upload,
  Home,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import type { KioskConfig } from '@/components/admin/KioskSettings';
import { DEFAULT_KIOSK_CONFIG } from '@/components/admin/KioskSettings';

// Extract fields from template elements
const extractFields = (template: LabelTemplate): string[] => {
  const fields = new Set<string>();
  template.elements.forEach(el => {
    if ('content' in el && typeof el.content === 'string') {
      const matches = el.content.matchAll(/\{\{(\w+)\}\}/g);
      for (const match of matches) {
        fields.add(match[1]);
      }
    }
    if ('field' in el && el.field) {
      fields.add(el.field);
    }
  });
  return Array.from(fields);
};

const KioskPage: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [selectedTemplate, setSelectedTemplate] = useState<LabelTemplate | null>(null);
  const [fieldData, setFieldData] = useState<FieldData>({});
  const [copies, setCopies] = useState(1);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [scannerActive, setScannerActive] = useState(true);
  const [lastScanned, setLastScanned] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [printPanelOpen, setPrintPanelOpen] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cfg, setCfg] = useState<KioskConfig>(DEFAULT_KIOSK_CONFIG);
  const [configLoaded, setConfigLoaded] = useState(false);
  
  const { savedTemplates, importTemplate } = useTemplateStorage();
  const { logActivity } = useActivityLogger();
  const { saveLabelPrint } = useLabelHistory();
  const fields = selectedTemplate ? extractFields(selectedTemplate) : [];

  // Load kiosk config
  useEffect(() => {
    const loadConfig = async () => {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'kiosk_config')
        .maybeSingle();
      if (data?.value && typeof data.value === 'object') {
        setCfg({ ...DEFAULT_KIOSK_CONFIG, ...(data.value as Record<string, unknown>) } as KioskConfig);
      }
      setConfigLoaded(true);
    };
    loadConfig();
  }, []);

  // Apply default copies from config
  useEffect(() => {
    if (configLoaded) {
      setCopies(cfg.defaultCopies);
      setScannerActive(cfg.enableBarcodeScanner);
    }
  }, [configLoaded]);

  // Auto-logout timer
  useEffect(() => {
    const minutes = cfg.autoLogoutMinutes;
    if (!minutes || minutes <= 0) return;

    let timeout: NodeJS.Timeout;
    const resetTimer = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        window.location.href = '/auth';
      }, minutes * 60000);
    };

    resetTimer();
    const events = ['mousedown', 'keydown', 'touchstart', 'mousemove'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    return () => {
      clearTimeout(timeout);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [cfg.autoLogoutMinutes]);

  // Resolve content with field data
  const resolveContent = useCallback((content: string) => {
    return content.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
      return fieldData[fieldName] || match;
    });
  }, [fieldData]);

  // Handle barcode scanner input
  const handleScan = useCallback((barcode: string) => {
    if (!selectedTemplate || fields.length === 0) return;
    
    setLastScanned(barcode);
    
    const currentField = fields[currentFieldIndex];
    if (currentField) {
      setFieldData(prev => ({ ...prev, [currentField]: barcode }));
      toast.success(`Scanned: ${barcode}`, { duration: 1500 });
      
      if (cfg.scannerAutoAdvance && currentFieldIndex < fields.length - 1) {
        setCurrentFieldIndex(prev => prev + 1);
        setTimeout(() => {
          inputRefs.current[currentFieldIndex + 1]?.focus();
        }, 50);
      }

      if (cfg.autoPrintOnLastField && currentFieldIndex === fields.length - 1) {
        setTimeout(() => handlePrint(), 200);
      }
    }
  }, [selectedTemplate, fields, currentFieldIndex, cfg.scannerAutoAdvance, cfg.autoPrintOnLastField]);

  useBarcodeScanner({
    onScan: handleScan,
    enabled: scannerActive && cfg.enableBarcodeScanner && !!selectedTemplate,
    minLength: 3,
    maxDelay: 50,
  });

  // Focus first input when template is selected
  useEffect(() => {
    if (selectedTemplate && fields.length > 0) {
      setCurrentFieldIndex(0);
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [selectedTemplate, fields.length]);

  const handlePrint = async () => {
    if (!selectedTemplate || isPrinting) return;
    
    setIsPrinting(true);
    try {
      const sheetConfig = createSingleLabelConfig(selectedTemplate.width, selectedTemplate.height);
      const data: FieldData[] = Array(copies).fill(fieldData);
      
      try {
        await printLabels(selectedTemplate, resolveContent, sheetConfig, data);
        toast.success('Print dialog opened');
        logActivity('kiosk_print', 'print', `Kiosk print: ${selectedTemplate.name} (${copies} copies)`, { templateName: selectedTemplate.name, copies });
        saveLabelPrint({ template: selectedTemplate, fieldData, copies, printSource: 'kiosk', sheetConfig });

        if (cfg.clearAfterPrint) {
          setTimeout(() => {
            setFieldData({});
            setCurrentFieldIndex(0);
            setCopies(cfg.defaultCopies);
            setLastScanned(null);
            inputRefs.current[0]?.focus();
          }, 500);
        }
      } catch (printError: any) {
        if (printError.message?.includes('popup')) {
          toast.error('Please allow popups to print labels', {
            description: 'Click the popup blocker icon in your browser address bar',
            duration: 5000,
          });
        } else {
          throw printError;
        }
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to print. Please try again.');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleReset = () => {
    setFieldData({});
    setCurrentFieldIndex(0);
    setCopies(cfg.defaultCopies);
    setLastScanned(null);
    inputRefs.current[0]?.focus();
  };

  const handleBack = () => {
    if (!cfg.showTemplateSelector) return;
    setSelectedTemplate(null);
    setFieldData({});
    setCurrentFieldIndex(0);
    setCopies(cfg.defaultCopies);
    setLastScanned(null);
  };

  const updateField = (field: string, value: string) => {
    setFieldData(prev => ({ ...prev, [field]: value }));
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleImportFile(file);
    }
    e.target.value = '';
  };

  const handleImportFile = async (file: File) => {
    try {
      const imported = await importTemplate(file);
      setSelectedTemplate(imported);
      toast.success(`Imported & saved: ${imported.name}`);
    } catch (error) {
      toast.error('Failed to import template. Make sure it\'s a valid JSON file.');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!cfg.showImportButton) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    if (!cfg.showImportButton) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.json')) {
      await handleImportFile(file);
    } else if (file) {
      toast.error('Please drop a JSON template file');
    }
  };

  // Determine which tabs are visible
  const showPresets = cfg.showPresetsTab;
  const showSaved = cfg.showSavedTemplatesTab;
  const hasBothTabs = showPresets && showSaved;

  // Count filled fields for mobile badge
  const filledCount = fields.filter(f => !!fieldData[f]).length;

  // ═══════════════════════════════════════
  // TEMPLATE SELECTION VIEW
  // ═══════════════════════════════════════
  if (!selectedTemplate) {
    return (
      <div 
        className="min-h-screen min-h-[100dvh] bg-workspace flex flex-col relative"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drag overlay */}
        {isDragging && cfg.showImportButton && (
          <div className="absolute inset-0 z-50 bg-primary/10 border-4 border-dashed border-primary flex items-center justify-center pointer-events-none">
            <div className="bg-card p-6 md:p-8 rounded-xl shadow-lg text-center">
              <Upload className="w-10 h-10 md:w-12 md:h-12 text-primary mx-auto mb-3" />
              <p className="text-lg md:text-xl font-semibold">Drop template file here</p>
              <p className="text-muted-foreground text-sm">JSON template files only</p>
            </div>
          </div>
        )}

        {/* Header */}
        <header className="h-14 md:h-16 border-b border-border bg-card flex items-center justify-between px-3 md:px-4 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {cfg.showBackToEditor && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="gap-1.5 shrink-0">
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">Editor</span>
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <LayoutTemplate className="w-5 h-5 md:w-6 md:h-6 text-primary" />
            <h1 className="text-base md:text-xl font-semibold truncate">{cfg.headerTitle || 'Kiosk Mode'}</h1>
          </div>
          <div className="flex items-center justify-end min-w-0">
            {cfg.showImportButton && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Button variant="outline" size="sm" onClick={handleImportClick} className="gap-2 shrink-0">
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Import</span>
                </Button>
              </>
            )}
          </div>
        </header>
        
        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-4xl mx-auto py-4 md:py-8 px-3 md:px-4">
            <div className="text-center mb-4 md:mb-6">
              <h2 className="text-xl md:text-2xl font-bold mb-1 md:mb-2">
                {cfg.welcomeMessage || 'Select a Label Template'}
              </h2>
              <p className="text-sm md:text-base text-muted-foreground">
                {cfg.welcomeSubtext || 'Choose a preset or your saved custom template'}
              </p>
            </div>
            
            {hasBothTabs ? (
              <Tabs defaultValue={cfg.defaultTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4 md:mb-6">
                  <TabsTrigger value="presets" className="gap-1.5 md:gap-2 text-sm">
                    <Sparkles className="w-4 h-4" />
                    Presets
                  </TabsTrigger>
                  <TabsTrigger value="saved" className="gap-1.5 md:gap-2 text-sm">
                    <FolderOpen className="w-4 h-4" />
                    <span className="hidden xs:inline">My </span>Templates
                    {savedTemplates.length > 0 && (
                      <Badge variant="secondary" className="ml-1 text-[10px]">
                        {savedTemplates.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="presets">
                  <TemplatePresets 
                    onSelectPreset={setSelectedTemplate} 
                    filterIds={cfg.allowedPresetIds.length > 0 ? cfg.allowedPresetIds : undefined} 
                  />
                </TabsContent>
                
                <TabsContent value="saved">
                  <SavedTemplatesList 
                    templates={savedTemplates} 
                    onSelect={setSelectedTemplate}
                    filterIds={cfg.allowedTemplateIds.length > 0 ? cfg.allowedTemplateIds : undefined}
                  />
                </TabsContent>
              </Tabs>
            ) : showPresets ? (
              <TemplatePresets 
                onSelectPreset={setSelectedTemplate} 
                filterIds={cfg.allowedPresetIds.length > 0 ? cfg.allowedPresetIds : undefined} 
              />
            ) : showSaved ? (
              <SavedTemplatesList 
                templates={savedTemplates} 
                onSelect={setSelectedTemplate}
                filterIds={cfg.allowedTemplateIds.length > 0 ? cfg.allowedTemplateIds : undefined}
              />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <LayoutTemplate className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No template sources enabled</p>
              </div>
            )}
          </div>
        </main>
        
        {/* Footer - hidden on mobile for more space */}
        <footer className="hidden sm:flex h-12 border-t border-border bg-card items-center justify-center shrink-0">
          <p className="text-xs text-muted-foreground">
            {cfg.footerText || 'Barcode scanner ready • Scan or select a template to begin'}
          </p>
        </footer>
      </div>
    );
  }

  // ═══════════════════════════════════════
  // DATA ENTRY VIEW
  // ═══════════════════════════════════════
  return (
    <div className="min-h-screen min-h-[100dvh] bg-workspace flex flex-col">
      {/* Header */}
      <header className="h-14 md:h-16 border-b border-border bg-card flex items-center justify-between px-3 md:px-4 shrink-0">
        {cfg.showTemplateSelector ? (
          <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1.5 shrink-0">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </Button>
        ) : cfg.showBackToEditor ? (
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="gap-1.5 shrink-0">
            <Home className="w-4 h-4" />
            <span className="hidden sm:inline">Editor</span>
          </Button>
        ) : (
          <div className="w-10" />
        )}
        
        <div className="flex items-center gap-2 min-w-0 flex-1 justify-center">
          <h1 className="text-base md:text-lg font-semibold truncate">{selectedTemplate.name}</h1>
          {isMobile && fields.length > 0 && (
            <Badge variant="secondary" className="text-[10px] shrink-0">
              {filledCount}/{fields.length}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2 shrink-0">
          {/* Reset button on mobile header */}
          {isMobile && (
            <Button variant="ghost" size="icon" onClick={handleReset} className="h-8 w-8">
              <RotateCcw className="w-4 h-4" />
            </Button>
          )}
          {cfg.enableBarcodeScanner ? (
            <Badge
              variant={scannerActive ? 'default' : 'secondary'}
              className={cn('gap-1 cursor-pointer text-[10px] md:text-xs', scannerActive && 'bg-primary')}
              onClick={() => setScannerActive(!scannerActive)}
            >
              <ScanBarcode className="w-3 h-3 md:w-3.5 md:h-3.5" />
              <span className="hidden sm:inline">Scanner </span>{scannerActive ? 'On' : 'Off'}
            </Badge>
          ) : (
            <div className="w-8" />
          )}
        </div>
      </header>

      {/* Main content area */}
      <main className={cn(
        "flex-1 overflow-hidden",
        isMobile ? "flex flex-col" : "flex gap-6 p-6"
      )}>
        {/* Data Entry Form */}
        <div className={cn(
          "flex flex-col min-w-0",
          isMobile ? "flex-1 overflow-hidden" : "flex-1"
        )}>
          {/* Desktop card header */}
          {!isMobile && (
            <Card className="flex-1 flex flex-col min-w-0">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Enter Label Data</span>
                  <Button variant="outline" size="sm" onClick={handleReset} className="gap-1.5">
                    <RotateCcw className="w-3.5 h-3.5" />
                    Reset
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                <ScrollArea className="h-full pr-4">
                  <div className="space-y-4">
                    {fields.map((field, index) => (
                      <FieldInput
                        key={field}
                        field={field}
                        index={index}
                        currentFieldIndex={currentFieldIndex}
                        fieldData={fieldData}
                        scannerActive={scannerActive}
                        enableBarcodeScanner={cfg.enableBarcodeScanner}
                        inputRefs={inputRefs}
                        fields={fields}
                        cfg={cfg}
                        setCurrentFieldIndex={setCurrentFieldIndex}
                        updateField={updateField}
                        handlePrint={handlePrint}
                        isMobile={false}
                      />
                    ))}
                    {fields.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        This template has no dynamic fields
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Mobile field list */}
          {isMobile && (
            <ScrollArea className="flex-1">
              <div className="p-3 space-y-3 pb-4">
                {fields.map((field, index) => (
                  <FieldInput
                    key={field}
                    field={field}
                    index={index}
                    currentFieldIndex={currentFieldIndex}
                    fieldData={fieldData}
                    scannerActive={scannerActive}
                    enableBarcodeScanner={cfg.enableBarcodeScanner}
                    inputRefs={inputRefs}
                    fields={fields}
                    cfg={cfg}
                    setCurrentFieldIndex={setCurrentFieldIndex}
                    updateField={updateField}
                    handlePrint={handlePrint}
                    isMobile={true}
                  />
                ))}
                {fields.length === 0 && (
                  <div className="text-center text-muted-foreground py-8 text-sm">
                    This template has no dynamic fields
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Desktop Print Controls Sidebar */}
        {!isMobile && (
          <Card className="w-72 shrink-0 flex flex-col">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Print Settings</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <div className="space-y-6 flex-1">
                <CopiesControl copies={copies} setCopies={setCopies} maxCopies={cfg.maxCopies} />

                <Separator />

                {cfg.autoPrintOnLastField && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded-md">
                    <Printer className="w-3.5 h-3.5" />
                    Auto-print enabled on last field
                  </div>
                )}

                {cfg.clearAfterPrint && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded-md">
                    <RotateCcw className="w-3.5 h-3.5" />
                    Fields clear after printing
                  </div>
                )}

                {lastScanned && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Last Scanned</Label>
                    <div className="p-3 bg-muted rounded-md font-mono text-sm break-all">
                      {lastScanned}
                    </div>
                  </div>
                )}
              </div>

              <Button
                size="lg"
                className="w-full h-16 text-lg gap-2 mt-4"
                onClick={handlePrint}
                disabled={isPrinting}
              >
                <Printer className={cn("w-5 h-5", isPrinting && "animate-pulse")} />
                {isPrinting ? 'Opening...' : copies > 1 ? `Print ${copies} Labels` : 'Print Label'}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Mobile: Sticky bottom print bar */}
      {isMobile && (
        <div className="border-t border-border bg-card shrink-0 safe-area-bottom">
          {/* Expandable print settings */}
          {printPanelOpen && (
            <div className="p-3 space-y-3 border-b border-border bg-card animate-in slide-in-from-bottom-2 duration-200">
              <CopiesControl copies={copies} setCopies={setCopies} maxCopies={cfg.maxCopies} compact />
              
              <div className="flex gap-2 flex-wrap">
                {cfg.autoPrintOnLastField && (
                  <Badge variant="outline" className="text-[10px] gap-1">
                    <Printer className="w-3 h-3" />
                    Auto-print
                  </Badge>
                )}
                {cfg.clearAfterPrint && (
                  <Badge variant="outline" className="text-[10px] gap-1">
                    <RotateCcw className="w-3 h-3" />
                    Auto-clear
                  </Badge>
                )}
              </div>

              {lastScanned && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">Last scan:</span>
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs truncate">{lastScanned}</span>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 p-3">
            {/* Toggle expand */}
            <Button
              variant="ghost"
              size="icon"
              className="h-12 w-10 shrink-0"
              onClick={() => setPrintPanelOpen(!printPanelOpen)}
            >
              {printPanelOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </Button>

            {/* Copies quick control */}
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={() => setCopies(Math.max(1, copies - 1))}
                disabled={copies <= 1}
              >
                <Minus className="w-3.5 h-3.5" />
              </Button>
              <span className="text-lg font-bold w-8 text-center">{copies}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10"
                onClick={() => setCopies(Math.min(cfg.maxCopies, copies + 1))}
                disabled={copies >= cfg.maxCopies}
              >
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>

            {/* Print button */}
            <Button
              size="lg"
              className="flex-1 h-12 text-base gap-2"
              onClick={handlePrint}
              disabled={isPrinting}
            >
              <Printer className={cn("w-5 h-5", isPrinting && "animate-pulse")} />
              {isPrinting ? 'Opening...' : copies > 1 ? `Print ${copies}` : 'Print'}
            </Button>
          </div>
        </div>
      )}

      {/* Desktop footer */}
      {!isMobile && (
        <footer className="h-12 border-t border-border bg-card flex items-center justify-center gap-4 shrink-0">
          {cfg.enableBarcodeScanner && (
            <>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <ScanBarcode className="w-4 h-4" />
                <span>Scan a barcode to fill the highlighted field</span>
              </div>
              <Separator orientation="vertical" className="h-4" />
            </>
          )}
          <div className="text-xs text-muted-foreground">
            Press Enter to advance • {selectedTemplate.width} × {selectedTemplate.height} mm
          </div>
        </footer>
      )}
    </div>
  );
};

// ═══════════════════════════════════════
// Extracted components
// ═══════════════════════════════════════

/** Reusable field input row */
const FieldInput: React.FC<{
  field: string;
  index: number;
  currentFieldIndex: number;
  fieldData: FieldData;
  scannerActive: boolean;
  enableBarcodeScanner: boolean;
  inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  fields: string[];
  cfg: KioskConfig;
  setCurrentFieldIndex: (i: number) => void;
  updateField: (field: string, value: string) => void;
  handlePrint: () => void;
  isMobile: boolean;
}> = ({ field, index, currentFieldIndex, fieldData, scannerActive, enableBarcodeScanner, inputRefs, fields, cfg, setCurrentFieldIndex, updateField, handlePrint, isMobile }) => (
  <div
    className={cn(
      'rounded-lg border-2 transition-colors',
      isMobile ? 'p-3' : 'p-4',
      currentFieldIndex === index
        ? 'border-primary bg-primary/5'
        : 'border-transparent bg-muted/30'
    )}
  >
    <div className="flex items-center gap-2 mb-1.5 md:mb-2">
      <Label className={cn("font-medium", isMobile ? "text-sm" : "text-base")}>{field}</Label>
      {fieldData[field] && (
        <CheckCircle2 className="w-4 h-4 text-primary" />
      )}
      {currentFieldIndex === index && scannerActive && enableBarcodeScanner && (
        <Badge variant="outline" className="text-[10px] ml-auto">
          Ready to scan
        </Badge>
      )}
    </div>
    <Input
      ref={(el) => { inputRefs.current[index] = el; }}
      value={fieldData[field] || ''}
      onChange={(e) => updateField(field, e.target.value)}
      onFocus={() => setCurrentFieldIndex(index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (index < fields.length - 1) {
            setCurrentFieldIndex(index + 1);
            inputRefs.current[index + 1]?.focus();
          } else if (cfg.autoPrintOnLastField) {
            handlePrint();
          }
        }
      }}
      placeholder={`Enter ${field}...`}
      className={cn(
        isMobile ? "text-base h-11" : "text-lg h-12"
      )}
    />
  </div>
);

/** Copies stepper control */
const CopiesControl: React.FC<{
  copies: number;
  setCopies: (n: number) => void;
  maxCopies: number;
  compact?: boolean;
}> = ({ copies, setCopies, maxCopies, compact }) => (
  <div className={cn("space-y-2", compact && "space-y-1")}>
    {!compact && <Label>Number of Copies</Label>}
    <div className="flex items-center gap-3">
      <Button
        variant="outline"
        size="icon"
        className={compact ? "h-9 w-9" : undefined}
        onClick={() => setCopies(Math.max(1, copies - 1))}
        disabled={copies <= 1}
      >
        <Minus className="w-4 h-4" />
      </Button>
      <Input
        type="number"
        value={copies}
        onChange={(e) => setCopies(Math.max(1, Math.min(maxCopies, parseInt(e.target.value) || 1)))}
        className={cn("text-center font-bold", compact ? "text-xl h-10" : "text-2xl h-14")}
        min={1}
        max={maxCopies}
      />
      <Button
        variant="outline"
        size="icon"
        className={compact ? "h-9 w-9" : undefined}
        onClick={() => setCopies(Math.min(maxCopies, copies + 1))}
        disabled={copies >= maxCopies}
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
    {maxCopies < 100 && (
      <p className="text-xs text-muted-foreground text-center">Max: {maxCopies}</p>
    )}
  </div>
);

/** Saved templates list sub-component */
const SavedTemplatesList: React.FC<{
  templates: LabelTemplate[];
  onSelect: (t: LabelTemplate) => void;
  filterIds?: string[];
}> = ({ templates, onSelect, filterIds }) => {
  const filtered = filterIds ? templates.filter(t => filterIds.includes(t.id)) : templates;

  if (filtered.length === 0) {
    return (
      <div className="text-center py-8 md:py-12 text-muted-foreground">
        <FolderOpen className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 md:mb-4 opacity-50" />
        <p className="text-base md:text-lg mb-2">No saved templates</p>
        <p className="text-xs md:text-sm">Create and save templates in the main editor to use them here</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
      {filtered.map((template) => (
        <Card
          key={template.id}
          className="cursor-pointer hover:border-primary active:scale-[0.98] transition-all"
          onClick={() => onSelect(template)}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-primary shrink-0" />
              <CardTitle className="text-sm truncate">{template.name}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription className="text-xs">
              {template.width} × {template.height} mm • {template.elements.length} elements
            </CardDescription>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default KioskPage;
